<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-card class="mx-auto" height="500" elevation="4">
          <v-card-title class="font-weight-bold light-blue-bg">
            <v-row class="mx-0" align="center">
              {{$t("home.noOfTrips")}}
              <v-spacer></v-spacer>
              <v-btn-toggle
                v-model="tripsByDayViewMode"
                mandatory
                dense
                rounded
                class="mr-2"
              >
                <v-btn value="aggregated" small>
                  {{$t("charts.aggregated")}}
                </v-btn>
                <v-btn value="detailed" small>
                  {{$t("charts.detailed")}}
                </v-btn>
              </v-btn-toggle>
              <v-menu
                v-model="startDateMenu"
                :close-on-content-click="false"
                :nudge-right="40"
                transition="scale-transition"
                offset-y
                min-width="auto"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="tripsStartDate"
                    :label="$t('home.startDate')"
                    prepend-icon="mdi-calendar"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    dense
                    hide-details
                    class="mr-2"
                    style="max-width: 150px;"
                  ></v-text-field>
                </template>
                <v-date-picker
                    v-model="tripsStartDate"
                    @input="startDateMenu = false"
                    :max="tripsEndDate"
                ></v-date-picker>
              </v-menu>
              <v-menu
                v-model="endDateMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
                left
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    v-model="tripsEndDate"
                    :label="$t('home.endDate')"
                    prepend-icon="mdi-calendar"
                    readonly
                    v-bind="attrs"
                    v-on="on"
                    dense
                    hide-details
                    style="max-width: 150px;"
                  ></v-text-field>
                </template>
                <v-date-picker
                    v-model="tripsEndDate"
                    @input="endDateMenu = false"
                    :min="tripsStartDate"
                    :max="today"
                ></v-date-picker>
              </v-menu>
            </v-row>
          </v-card-title>
          <v-skeleton-loader
            :loading="numberOfTripsLoading"
            :transition-group="'scale-transition'"
            type="list-item@7"
          >
            <BarChart
              :chart-data="tripsByDayChartData"
              :options="tripsByDayDataOptions"
            ></BarChart>
          </v-skeleton-loader>
        </v-card>
      </v-col>
      <v-col cols="12" md="6">
        <v-card elevation="4">
          <v-card-title class="font-weight-bold light-blue-bg">
            {{$t("home.transportEventErrors")}}
          </v-card-title>
          <v-skeleton-loader
            :loading="failedValidationsLoading"
            :transition-group="'scale-transition'"
            type="list-item@3"
          >
            <v-simple-table height="500px">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-left">{{$t("home.errorType")}}</th>
                    <th class="text-left">{{$t("home.errorCount")}}</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="failReason in failReasons"
                    :key="failReason.item1"
                    @click="searchFailed(failReason.item1)"
                    class="clickable"
                  >
                    <td>{{ failReason.item1 | validationFailReasonToText }}</td>
                    <td class="error--text font-weight-bold">
                      {{ failReason.item2 }}
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </v-skeleton-loader>
        </v-card>
      </v-col>
      <v-col cols="12" md="6">
        <v-card class="mx-auto" height="500px" elevation="4">
          <v-card-title class="font-weight-bold light-blue-bg">
            <v-row class="mx-0" align="center">
              {{$t("home.statusesOfLastXDays", {days: tripStatusesFromDays})}}
              <v-spacer></v-spacer>
              <v-btn-toggle
                v-model="tripStatusesFromDays"
                mandatory
                dense
                rounded
              >
                <v-btn value="30" small>
                  30
                </v-btn>
                <v-btn value="60" small>
                  60
                </v-btn>
                <v-btn value="90" small>
                  90
                </v-btn>
              </v-btn-toggle>
            </v-row>
          </v-card-title>
          <v-skeleton-loader
            :loading="tripStatusesLoading"
            :transition-group="'scale-transition'"
            type="list-item@5"
          >
            <DoughnutChart
              :data="tripsByTimeRangeData"
              :options="tripsByTimeRangeDataOptions"
            ></DoughnutChart>
          </v-skeleton-loader>
        </v-card>
      </v-col>
    </v-row>
    <v-row >

    </v-row>
  </v-container>
</template>

<script>
import { TranportEventValidationErrorMixin } from "@/models/ValidationErrors";
import BarChart from "@/components/Charts/BarChart";
import DoughnutChart from "@/components/Charts/DoughnutChart";
import moment from "moment";

// Shared status category configuration for both bar chart and doughnut chart
const STATUS_CATEGORIES = {
    normal: [
        { status: 5, labelKey: "charts.purchaseInvoiceSent", color: "#B5E48C" },
        { status: 4, labelKey: "charts.salesInvoiceSent", color: "#1B4332" },
        { status: 3, labelKey: "charts.waitingForInvoicing", color: "#1D3557" },
        { status: 8, labelKey: "charts.receiptMissing", color: "#ECECEC" },
        { status: 9, labelKey: "charts.orderMissing", color: "#4A4A4A" },
        { status: 2, labelKey: "charts.validationFailed", color: "#E63946" }
    ],
    kela: [
        { status: 5, labelKey: "charts.purchaseInvoiceSent", color: "#B5E48C" },
        { status: 4, labelKey: "charts.salesInvoiceSent", color: "#00A86B" },
        { status: 3, labelKey: "charts.waitingForInvoicing", color: "#1B4332" },
        { status: 7, labelKey: "charts.waitingForExternalValidationKela", color: "#3E7CB1" },
        { status: 6, labelKey: "charts.readyForExternalValidationKela", color: "#1D3557" },
        { status: 8, labelKey: "charts.receiptMissing", color: "#ECECEC" },
        { status: 9, labelKey: "charts.orderMissing", color: "#4A4A4A" },
        { status: 2, labelKey: "charts.validationFailed", color: "#E63946" }
    ]
};

export default {
    mixins: [TranportEventValidationErrorMixin],
    components: { BarChart, DoughnutChart },
    data() {
        return {
            numberOfTripsLoading: true,
            tripStatusesLoading: true,
            failedValidationsLoading: true,
            salesInvoicesLoading: true,
            purchaseInvoicesLoading: true,
            tripStatusesFromDays: 30,
            tripsByDayViewMode: "detailed",
            tripsStartDate: moment().subtract(14, "days").format("YYYY-MM-DD"),
            tripsEndDate: moment().format("YYYY-MM-DD"),
            startDateMenu: false,
            endDateMenu: false,
            tripsByDayDataOptions: {
                maintainAspectRatio: false,
                scales: {
                    xAxes: [
                        {
                            stacked: true
                        }
                    ],
                    yAxes: [
                        {
                            stacked: true
                        }
                    ]
                },
                tooltips: {
                    callbacks: {
                        afterLabel: function(tooltipItem, data) {
                            return (
                                data["datasets"][tooltipItem["datasetIndex"]]["charges"][tooltipItem["index"]] + " €"
                            );
                        }
                    }
                }
            },
            tripsByTimeRangeDataOptions: {
                maintainAspectRatio: false,
                tooltips: {
                    callbacks: {
                        afterLabel: function(tooltipItem, data) {
                            return (
                                data["datasets"][0]["charges"][tooltipItem["index"]] +
                " €"
                            );
                        }
                    }
                }
            }
        };
    },
    computed: {
        today() {
            return moment().format("YYYY-MM-DD");
        },
        tenant() {
            return this.$store.state.user.tenant;
        },
        failReasons() {
            return this.$store.state.invoicing.failReasons;
        },
        // Aggregated view (old 4 categories)
        tripsByDayDataAggregated() {
            let statuses = this.$store.state.invoicing.tripStatusesByDay;
            if (!statuses) return;

            const labels = statuses.map(s => moment(s.item1).format("DD.MM."));

            return {
                labels: labels,
                datasets: [
                    {
                        label: this.$t("charts.valid"),
                        backgroundColor: "#1D3557",
                        data: statuses.map(s =>
                            s.item2
                                .filter(e => e.item1 > 2 && e.item1 < 8)
                                .map(e => e.item2)
                                .reduce((a, b) => a + b, 0)
                        ),
                        charges: statuses.map(s =>
                            s.item2
                                .filter(e => e.item1 > 2 && e.item1 < 8)
                                .map(e => e.item3)
                                .reduce((a, b) => a + b, 0)
                        )
                    },
                    {
                        label: this.$t("charts.receiptMissing"),
                        backgroundColor: "#ECECEC",
                        data: statuses.map(s =>
                            s.item2
                                .filter(e => e.item1 === 8)
                                .map(e => e.item2)
                                .reduce((a, b) => a + b, 0)
                        ),
                        charges: statuses.map(s =>
                            s.item2
                                .filter(e => e.item1 === 8)
                                .map(e => e.item3)
                                .reduce((a, b) => a + b, 0)
                        )
                    },
                    {
                        label: this.$t("charts.orderMissing"),
                        backgroundColor: "#4A4A4A",
                        data: statuses.map(s =>
                            s.item2
                                .filter(e => e.item1 === 9)
                                .map(e => e.item2)
                                .reduce((a, b) => a + b, 0)
                        ),
                        charges: statuses.map(s =>
                            s.item2
                                .filter(e => e.item1 === 9)
                                .map(e => e.item3)
                                .reduce((a, b) => a + b, 0)
                        )
                    },
                    {
                        label: this.$t("charts.validationFailed"),
                        backgroundColor: "#E63946",
                        data: statuses.map(s =>
                            s.item2
                                .filter(e => e.item1 === 2)
                                .map(e => e.item2)
                                .reduce((a, b) => a + b, 0)
                        ),
                        charges: statuses.map(s =>
                            s.item2
                                .filter(e => e.item1 === 2)
                                .map(e => e.item3)
                                .reduce((a, b) => a + b, 0)
                        )
                    }
                ]
            };
        },
        // Detailed view (new 6 categories)
        tripsByDayDataDetailed() {
            let statuses = this.$store.state.invoicing.tripStatusesByDay;
            if (!statuses) return;

            const categories = STATUS_CATEGORIES.normal;
            const labels = statuses.map(s => moment(s.item1).format("DD.MM."));

            const datasets = categories.map(category =>
                this.createDatasetForStatus(
                    statuses,
                    category.status,
                    category.labelKey,
                    category.color
                )
            );

            return {
                labels: labels,
                datasets: datasets
            };
        },
        // Detailed view for Kela (new 8 categories)
        kelaTripsByDayDataDetailed() {
            let statuses = this.$store.state.invoicing.tripStatusesByDay;
            if (!statuses) return;

            const categories = STATUS_CATEGORIES.kela;
            const labels = statuses.map(s => moment(s.item1).format("DD.MM."));

            const datasets = categories.map(category =>
                this.createDatasetForStatus(
                    statuses,
                    category.status,
                    category.labelKey,
                    category.color
                )
            );

            return {
                labels: labels,
                datasets: datasets
            };
        },
        // Main computed property for chart binding
        tripsByDayChartData() {
            if (this.tripsByDayViewMode === "aggregated") {
                return this.tripsByDayDataAggregated;
            }

            // Detailed view
            if (this.tenant === "taksikuutio" || this.tenant === "pst-kela") {
                return this.kelaTripsByDayDataDetailed;
            }
            return this.tripsByDayDataDetailed;
        },
        tripsByTimeRangeData() {
            if (this.$store.state.user.tenant === "taksikuutio" || this.$store.state.user.tenant === "pst-kela") return this.kelaTripsByTimeRangeData;
            let statuses = this.$store.state.invoicing.tripStatusesByTimeRange;
            if (!statuses) return;

            const categories = STATUS_CATEGORIES.normal;

            let orderedStatuses = categories.map(cat =>
                statuses.find(s => s.item1 === cat.status)
            );

            let labels = categories.map(cat => this.$t(cat.labelKey));
            let backgroundColors = categories.map(cat => cat.color);

            return {
                labels: labels,
                datasets: [
                    {
                        data: orderedStatuses.map(s => s.item2),
                        backgroundColor: backgroundColors,
                        charges: orderedStatuses.map(s => s.item3)
                    }
                ]
            };
        },
        kelaTripsByTimeRangeData() {
            let statuses = this.$store.state.invoicing.tripStatusesByTimeRange;
            if (!statuses) return;

            const categories = STATUS_CATEGORIES.kela;

            let orderedStatuses = categories.map(cat =>
                statuses.find(s => s.item1 === cat.status)
            );

            let labels = categories.map(cat => this.$t(cat.labelKey));
            let backgroundColors = categories.map(cat => cat.color);

            return {
                labels: labels,
                datasets: [
                    {
                        data: orderedStatuses.map(s => s.item2),
                        backgroundColor: backgroundColors,
                        charges: orderedStatuses.map(s => s.item3)
                    }
                ]
            };
        }
    },
    watch: {
        tripStatusesFromDays: function(val) {
            this.getTransportEventStatuses(val);
        },
        tripsStartDate: function() {
            this.getTrips();
        },
        tripsEndDate: function() {
            this.getTrips();
        },
        tenant(val) {
            this.updateGraphs();
        }
    },
    methods: {
        createDatasetForStatus(statuses, statusCode, label, backgroundColor) {
            return {
                label: this.$t(label),
                backgroundColor: backgroundColor,
                data: statuses.map(s =>
                    s.item2
                        .filter(e => e.item1 === statusCode)
                        .map(e => e.item2)
                        .reduce((a, b) => a + b, 0)
                ),
                charges: statuses.map(s =>
                    s.item2
                        .filter(e => e.item1 === statusCode)
                        .map(e => e.item3)
                        .reduce((a, b) => a + b, 0)
                )
            };
        },
        async updateGraphs() {
            this.getFailReasons();
            this.getTrips();
            this.getTransportEventStatuses();
        },
        async getFailReasons() {
            this.failedValidationsLoading = true;
            this.$store
                .dispatch("invoicing/getFailReasons")
                .catch(error => {
                    this.snackbar = true;
                    this.snackbarColor = "error";
                    this.snackbarText =
            "Getting validation errors failed. Error: " + error;
                })
                .finally(() => {
                    this.failedValidationsLoading = false;
                });
        },
        async getTransportEventStatuses(days = 30) {
            this.tripStatusesLoading = true;
            let maxDate = moment();
            let minDate = moment(maxDate).subtract(days, "days");
            this.$store
                .dispatch("invoicing/getTripStatusesByTimeRange", {
                    start: minDate.format("YYYY-MM-DDThh:mm"),
                    end: maxDate.format("YYYY-MM-DDThh:mm")
                })
                .catch(error => {
                    this.snackbar = true;
                    this.snackbarColor = "error";
                    this.snackbarText =
            "Getting trip statuses by day failed. Error: " + error;
                })
                .finally(() => {
                    this.tripStatusesLoading = false;
                });
        },
        async getTrips() {
            this.numberOfTripsLoading = true;
            let startDate = moment(this.tripsStartDate).startOf("day");
            let endDate = moment(this.tripsEndDate).endOf("day");
            this.$store
                .dispatch("invoicing/getTripStatusesByDay", {
                    start: startDate.format("YYYY-MM-DDT00:00:00"),
                    end: endDate.format("YYYY-MM-DDT23:59:59")
                })
                .catch(error => {
                    this.snackbar = true;
                    this.snackbarColor = "error";
                    this.snackbarText =
            "Getting trip statuses by day failed. Error: " + error;
                })
                .finally(() => {
                    this.numberOfTripsLoading = false;
                });
        },
        searchFailed(id) {
            this.$router.push({
                name: "Invoicing",
                query: { validationStates: [4, 6], validationFailReasonCodes: id }
            });
        },
        searchSalesInvoice(id) {
            this.$router.push({
                name: "Invoicing",
                query: { salesInvoiceNumbers: id }
            });
        },
        searchPurchaseInvoice(id) {
            this.$router.push({
                name: "Invoicing",
                query: { purchaseInvoiceNumbers: id }
            });
        }
    },
    created() {
        this.updateGraphs();
    }
};
</script>

<style scoped>
.clickable:hover {
  cursor: pointer;
}

.light-blue-bg {
  background-color: #ebf3fa;
  color: #333333;
}
</style>
